/**
 * Umowy o dzieło i zgłoszenia ZUS RUD (Etap 47, ADR-0048).
 *
 * Art. 36 ust. 17 ustawy o systemie ubezpieczeń społecznych: o zawarciu każdej
 * umowy o dzieło trzeba poinformować ZUS w 7 dni od dnia jej ZAWARCIA — nie od
 * rozpoczęcia wykonywania dzieła. Karta trzyma trzy różne daty, pokazuje termin
 * i stan zgłoszenia, a wydruk formularza jest pomocniczy: samo zgłoszenie
 * składa człowiek na PUE ZUS.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  EmptyState,
  FormField,
  InfoBanner,
  Input,
  Spinner,
  StatusPill,
  useToast,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import { api, type DzieloContractInput, apiYear } from '../../api/client'
import styles from './FamilyMembers.module.css'

export interface DzieloContractsProps {
  employeeId: string
  /** Wpis i zgłoszenie wymagają uprawnienia do edycji akt. */
  canEdit: boolean
  /**
   * Czy karta dotyczy rodzaju umowy osoby (fala 8, grupa 87; D98). Przy
   * „nie dotyczy" karta pokazuje tylko istniejące wpisy, do odczytu, a bez
   * wpisów nie renderuje się wcale.
   */
  applicable?: boolean
}

function currentYear(): number {
  return apiYear()
}

function emptyDraft(): Omit<DzieloContractInput, 'employeeId'> {
  return { concludedOn: '', startsOn: '', endsOn: '', subject: '' }
}

export function DzieloContracts({ employeeId, canEdit: canEditProp, applicable = true }: DzieloContractsProps) {
  const canEdit = canEditProp && applicable
  const { t } = useTranslation()
  const toast = useToast()
  const queryClient = useQueryClient()
  const ids = {
    year: useId(),
    concluded: useId(),
    starts: useId(),
    ends: useId(),
    subject: useId(),
    amount: useId(),
  }

  const [year, setYear] = useState(currentYear)
  const [draft, setDraft] = useState<Omit<DzieloContractInput, 'employeeId'> | null>(null)
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  const register = useQuery({
    queryKey: ['dzielo-contracts', year],
    queryFn: () => api.dzieloContracts(year),
  })

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['dzielo-contracts'] })
    void queryClient.invalidateQueries({ queryKey: ['personnel-file', employeeId] })
  }

  const record = useMutation({
    mutationFn: (values: Omit<DzieloContractInput, 'employeeId'>) =>
      api.recordDzieloContract({
        ...values,
        employeeId,
        ...(amount.trim() === ''
          ? {}
          : { amountGr: Math.round(Number(amount.replace(',', '.')) * 100) }),
      }),
    onSuccess: () => {
      toast.show(t('dzielo.saved'), { tone: 'success' })
      setDraft(null)
      setAmount('')
      setError(null)
      refresh()
    },
    onError: () => setError('dzielo.errorGeneric'),
  })

  const report = useMutation({
    mutationFn: (contractId: string) => api.reportDzieloContract(contractId),
    onSuccess: () => {
      toast.show(t('dzielo.reported'), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: () => setError('dzielo.errorGeneric'),
  })

  const issue = useMutation({
    mutationFn: () => api.issueDzieloForm(employeeId),
    onSuccess: (result) => {
      toast.show(t('dzielo.formIssued', { count: result.contracts }), { tone: 'success' })
      setError(null)
      refresh()
    },
    onError: () => setError('dzielo.errorGeneric'),
  })

  const mine = (register.data?.items ?? []).filter((item) => item.employeeId === employeeId)
  const pending = mine.some((item) => item.status === 'todo' || item.status === 'overdue')

  /* Karta „nie dotyczy” bez wpisów nie miga też w trakcie ładowania. */
  if (!applicable && mine.length === 0) return null

  return (
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>{t('dzielo.title')}</h3>
        {canEdit && draft === null ? (
          <Button size="sm" variant="secondary" onClick={() => setDraft(emptyDraft())}>
            {t('dzielo.add')}
          </Button>
        ) : null}
      </div>
      <p className={styles.note}>{t('dzielo.lead')}</p>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      <div className={styles.formGrid}>
        <FormField label={t('dzielo.year')} htmlFor={ids.year}>
          <Input
            id={ids.year}
            value={String(year)}
            onChange={(event) => setYear(Number(event.target.value) || currentYear())}
          />
        </FormField>
      </div>

      {register.isPending ? (
        <Spinner label={t('dzielo.loading')} />
      ) : mine.length === 0 ? (
        <EmptyState title={t('dzielo.empty')} description={t('dzielo.emptyBody')} />
      ) : (
        <>
          <Card pad="flush" className={styles.list}>
            {mine.map((item) => (
              <div key={item.id} className={styles.row}>
                <span className={styles.rowName}>{formatDate(item.concludedOn, 'dot')}</span>
                <span className={styles.rowMeta}>{item.subject}</span>
                <span className={styles.rowMeta}>
                  {t('dzielo.period', {
                    from: formatDate(item.startsOn, 'dot'),
                    to: formatDate(item.endsOn, 'dot'),
                  })}
                </span>
                <span className={styles.rowMeta}>
                  {item.amountGr === null ? '' : formatPLN(item.amountGr)}
                </span>
                <span className={styles.rowMeta}>
                  {t('dzielo.deadline', { date: formatDate(item.deadline, 'dot') })}
                </span>
                <StatusPill
                  tone={
                    item.status === 'overdue'
                      ? 'danger'
                      : item.status === 'reported'
                        ? 'success'
                        : item.status === 'reported_late'
                          ? 'warning'
                          : 'neutral'
                  }
                >
                  {t(`dzielo.status.${item.status}`)}
                </StatusPill>
                {canEdit && item.reportedOn === null ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={report.isPending}
                    onClick={() => report.mutate(item.id)}
                  >
                    {t('dzielo.markReported')}
                  </Button>
                ) : null}
              </div>
            ))}
          </Card>
          {canEdit ? (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                disabled={issue.isPending}
                onClick={() => {
                  setError(null)
                  issue.mutate()
                }}
              >
                {issue.isPending ? <Spinner label={t('dzielo.saving')} /> : t('dzielo.issueForm')}
              </Button>
            </div>
          ) : null}
          {pending ? (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('dzielo.pendingWarning')}
            </InfoBanner>
          ) : null}
        </>
      )}

      {draft !== null ? (
        <>
          <div className={styles.formGrid}>
            <FormField
              label={t('dzielo.fields.concludedOn')}
              htmlFor={ids.concluded}
              hint={t('dzielo.fields.concludedHint')}
            >
              <Input
                id={ids.concluded}
                type="date"
                value={draft.concludedOn}
                onChange={(event) => setDraft({ ...draft, concludedOn: event.target.value })}
              />
            </FormField>
            <FormField label={t('dzielo.fields.startsOn')} htmlFor={ids.starts}>
              <Input
                id={ids.starts}
                type="date"
                value={draft.startsOn}
                onChange={(event) => setDraft({ ...draft, startsOn: event.target.value })}
              />
            </FormField>
            <FormField label={t('dzielo.fields.endsOn')} htmlFor={ids.ends}>
              <Input
                id={ids.ends}
                type="date"
                value={draft.endsOn}
                onChange={(event) => setDraft({ ...draft, endsOn: event.target.value })}
              />
            </FormField>
            <FormField
              label={t('dzielo.fields.subject')}
              htmlFor={ids.subject}
              hint={t('dzielo.fields.subjectHint')}
            >
              <Input
                id={ids.subject}
                value={draft.subject}
                onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
              />
            </FormField>
            <FormField label={t('dzielo.fields.amount')} htmlFor={ids.amount}>
              <Input
                id={ids.amount}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </FormField>
          </div>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setDraft(null)}>
              {t('dzielo.cancel')}
            </Button>
            <Button
              disabled={
                record.isPending ||
                draft.concludedOn === '' ||
                draft.startsOn === '' ||
                draft.endsOn === '' ||
                draft.subject.trim().length < 3
              }
              onClick={() => {
                setError(null)
                record.mutate(draft)
              }}
            >
              {record.isPending ? <Spinner label={t('dzielo.saving')} /> : t('dzielo.save')}
            </Button>
          </div>
        </>
      ) : null}
    </section>
  )
}
